/**
 * PropuestaEntidadCard — el bloque Confirmar/Descartar del chat (MP5-FRONT-CONFIRMAR,
 * orden 96b62f45). Cierra el lazo chat→crear-entidad: el orquestador del back PROPONE
 * (y, con el OK del usuario en la conversación, crea un BORRADOR descartable); esta tarjeta
 * deja que el HUMANO lo confirme o lo descarte con SU sesión.
 *
 * Dos fases del contrato `data.propuesta_entidad` (shape MF-SHAPE-RATIFICADO):
 *   - 'preview'  → semáforo C1 en vivo + resumen, SIN botones (transparencia de "proponer
 *                  antes de comprometer" mientras el usuario itera con el chat).
 *   - 'borrador' → ya hay wizard_id + acciones: salen Confirmar (VALIDAR) y Descartar.
 *
 * Doctrina (inviolable):
 *   - Gate 1: el VALIDAR/DESCARTAR los dispara el CLICK del humano con el token del
 *     navegador (useAuthStore) — el agente jamás valida. El chat solo propone.
 *   - §33.5: la ruta y su versión VIENEN del back (acciones.*.path ya resuelto). El front
 *     NUNCA construye ruta ni versión — solo hace el POST de lo que recibe.
 *   - Recibir la ruta no otorga permiso: un grant ausente da 403 y se explica con la madre
 *     `explicarError` (render gracioso), no se vuelca jerga del servidor.
 */
import { useState } from 'react'
import { CheckCircle2, XCircle, Circle, Loader2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { explicarError, toastError } from '@/lib/errores'
import { componerBodyDescarte } from '@/lib/propuesta-entidad'
import { useAuthStore } from '@/stores/auth'
import type { PropuestaEntidad } from '@/stores/chat'
import { Button } from './ui/button'

/** Etiqueta humana de un campo requerido del descarte (hoy solo razon_descarte). */
function etiquetaCampo(key: string): string {
  if (key === 'razon_descarte') return 'Motivo del descarte'
  const s = key.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

type Estado = 'idle' | 'confirmando' | 'confirmada' | 'descartando' | 'descartada'

function Chequeo({ ok, label }: { ok: boolean | undefined; label: string }) {
  // undefined = el back no reportó ese chequeo: no afirmamos nada (Circle neutro).
  if (ok === undefined) return null
  return (
    <span className={`inline-flex items-center gap-1 ${ok ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
      {label}
    </span>
  )
}

export function PropuestaEntidadCard({ propuesta }: { propuesta: PropuestaEntidad }) {
  const token = useAuthStore((s) => s.token)
  const [estado, setEstado] = useState<Estado>('idle')
  const [modoDescarte, setModoDescarte] = useState(false)
  const [valores, setValores] = useState<Record<string, string>>({})
  const [errorInline, setErrorInline] = useState<string | null>(null)

  const { fase, resumen, semaforo, acciones, wizard_id } = propuesta
  const camposRequeridos = propuesta.campos_requeridos || []
  const esBorrador = fase === 'borrador' && !!acciones

  async function confirmar() {
    if (!acciones) return
    setEstado('confirmando'); setErrorInline(null)
    try {
      // §33.5: la ruta + versión vienen del back; aquí solo se ejecuta. Gate 1: token del navegador.
      await api.post(acciones.confirmar.path, token, acciones.confirmar.body)
      setEstado('confirmada')
      toast.success('Creación confirmada · la entidad se está dando de alta')
    } catch (e) {
      setEstado('idle')
      setErrorInline(explicarError(e).mensaje)
      toastError(e, 'Confirmar creación')
    }
  }

  async function descartar() {
    if (!acciones) return
    // Reúne los campos que el back exige (DMN validar_descarte_borrador) en payload.
    const compuesto = componerBodyDescarte(acciones.descartar.body, camposRequeridos, valores)
    if (!compuesto.ok) { setErrorInline(`Indica ${etiquetaCampo(compuesto.faltante).toLowerCase()}.`); return }
    setEstado('descartando'); setErrorInline(null)
    try {
      await api.post(acciones.descartar.path, token, compuesto.body)
      setEstado('descartada')
      toast.success('Borrador descartado')
    } catch (e) {
      setEstado('idle')
      setErrorInline(explicarError(e).mensaje)
      toastError(e, 'Descartar borrador')
    }
  }

  return (
    <div className="rounded-lg bg-card shadow-md ring-1 ring-black/5 dark:ring-white/10 p-3 space-y-2.5">
      {/* Cabecera: nombre §7 autocompuesto + chip de fase. */}
      <div className="flex items-start gap-2">
        <Sparkles className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[13px] font-semibold break-all">{resumen.nombre}</div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
            {fase === 'preview' ? 'Propuesta — aún no creada' : 'Borrador listo para confirmar'}
          </div>
        </div>
      </div>

      {/* Semáforo C1 del molde (BSK-4): verde global solo si el molde pasa. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <span className={`inline-flex items-center gap-1 font-medium ${semaforo.ok ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
          {semaforo.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {semaforo.ok ? 'Molde correcto' : 'Molde incompleto'}
        </span>
        <Chequeo ok={semaforo.loop} label="loop" />
        <Chequeo ok={semaforo.terminal} label="terminal" />
        <Chequeo ok={semaforo.descarte_s13} label="descarte S13" />
      </div>

      {/* Resumen de la FSM propuesta. */}
      <div className="text-xs text-muted-foreground">
        {resumen.n_estados} estados · {resumen.n_transiciones} transiciones · {resumen.n_campos} campos · {resumen.n_indices} índices
      </div>

      {/* Estados terminales del flujo (confirmada / descartada). */}
      {estado === 'confirmada' ? (
        <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-400">
          <CheckCircle2 className="h-4 w-4" />
          Confirmada. La entidad se está dando de alta; aparecerá en tu catálogo en unos segundos.
        </div>
      ) : estado === 'descartada' ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <XCircle className="h-4 w-4" />
          Borrador descartado. Puedes pedirle otra propuesta al asistente.
        </div>
      ) : esBorrador ? (
        <>
          {/* Aviso de quién ejecuta (gate 1) + los 2 botones. */}
          {!modoDescarte && (
            <>
              <p className="text-[11px] text-muted-foreground">
                Al confirmar, la creación la realizas tú con tu sesión; el sistema completa el alta.
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={confirmar} disabled={estado === 'confirmando' || !semaforo.ok}
                  title={semaforo.ok ? 'Crear la entidad con tu sesión' : 'El molde no está limpio: el asistente debe corregir la propuesta'}>
                  {estado === 'confirmando' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Confirmar creación
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setModoDescarte(true); setErrorInline(null) }} disabled={estado === 'confirmando'}>
                  <XCircle className="h-4 w-4" />
                  Descartar
                </Button>
              </div>
            </>
          )}

          {/* Form de descarte: el back exige razon_descarte (campos_requeridos). */}
          {modoDescarte && (
            <div className="space-y-2">
              {camposRequeridos.map((c) => (
                <label key={c} className="block space-y-1">
                  <span className="text-[11px] font-medium text-muted-foreground">{etiquetaCampo(c)}</span>
                  <textarea
                    value={valores[c] || ''}
                    onChange={(e) => setValores((v) => ({ ...v, [c]: e.target.value }))}
                    rows={2}
                    autoFocus
                    placeholder="¿Por qué descartas esta propuesta?"
                    className="w-full px-2.5 py-1.5 text-xs rounded-md bg-background shadow-sm ring-1 ring-black/5 dark:ring-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                  />
                </label>
              ))}
              <div className="flex items-center gap-2">
                <Button size="sm" variant="destructive" onClick={descartar} disabled={estado === 'descartando'}>
                  {estado === 'descartando' ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  Confirmar descarte
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setModoDescarte(false); setErrorInline(null) }} disabled={estado === 'descartando'}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </>
      ) : null}

      {errorInline && <p className="text-[11px] text-destructive">{errorInline}</p>}

      {/* Trazabilidad: el UUID del borrador (discreto), como en el resto de la app. */}
      {wizard_id && <div className="text-[10px] text-muted-foreground/60 font-mono">borrador {wizard_id.slice(0, 8)}</div>}
    </div>
  )
}
